<template>
  <div class="role-purpose-tab">
    <ExBreadcrumbs
      class="mb-5 mb-md-6"
      :breadcrumbsList="breadcrumbsList"
      @onClickBreadcrumb="handleBreadcrumbClick"
    />
    <div class="business-purpose-title">
      Business Purpose of the Organization
    </div>
    <div class="iq-assist-banner">
      <div class="iq-assist-banner-icon-img-wrapper">
        <img :src="require('@components/assets/info-circle.svg')" alt="" class="logo-img">
      </div>
      <div class="iq-assist-banner-content">
        <div class="iq-assist-banner-title">Provide better Assistance</div>
        <div class="iq-assist-banner-description">
          IQ uses business purpose as context to provide better answers
        </div>
      </div>
    </div>
    <!--
      ER-10219: Company websites power the account-context suggestion. They live
      on the Account (`company_websites`) and are edited here one URL at a time.
    -->
    <CompanyWebsitesField
      class="mt-6"
      :websites="companyWebsites"
      :scan-statuses="companyWebsiteScanStatuses"
      :saving="savingCompanyWebsites"
      :error="companyWebsitesError"
      :disabled="disabled"
      :can-scan-now="canScanCompanyWebsites"
      :scan-requesting="scanningCompanyWebsites"
      @add="(url) => emit('add-website', url)"
      @remove="(url) => emit('remove-website', url)"
      @scan-now="() => emit('scan-websites')"
    />
    <!--
      ER-9892 / ER-9901 / MDP-494: when idea-MDP-494 is on, mount ContextTab
      (ContextFiles + ContextEditor) against account.context; otherwise keep
      the legacy PurposeViewEdit UI with the same account.context value.
    -->
    <ContextTab
      v-if="showContextTabV2"
      class="mt-6"
      title="Account Context"
      :state="contextState"
      :context="context"
      :context-updated-at="contextUpdatedAt"
      :context-updated-by="contextUpdatedBy"
      :files="files"
      :uploading-files="uploadingFiles"
      :disabled="disabled"
      :draft="accountContextDraft"
      :max-length="ENTITY_CONTEXT_MAX_LENGTH"
      :suggesting="accountContextSuggesting"
      :suggest-error="accountContextSuggestError"
      :suggest-hint="accountSuggestHint"
      @save="handleContextSave"
      @suggest="() => emit('suggest')"
      @improve="() => emit('suggest')"
      @input="(value) => emit('context-input', value)"
      @discard="() => emit('context-discard')"
      @upload="(fileList) => emit('upload', fileList)"
      @remove="(fileId) => emit('remove', fileId)"
    />
    <PurposeViewEdit
      v-else
      class="mt-6"
      :value="purposeOrContext"
      placeholder="Enter purpose"
      :maxLength="3000"
      :isSaving="disabled"
      :showTiptapEditor="true"
      @save="handleSave"
      @discard="handleDiscard"
      @close="handleClose"
      @add-purpose="() => emit('add-purpose')"
      @edit="() => emit('edit')"
      @improve="(val) => emit('improve', val)"
      @suggest="() => emit('suggest')"
      hideAIButtons="true"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import PurposeViewEdit from "@components/PurposeViewEdit/PurposeViewEdit.vue";
import ContextTab from "@components/ContextTab/ContextTab.vue";
import CompanyWebsitesField from "@components/CompanyWebsitesField/CompanyWebsitesField.vue";
import { ENTITY_CONTEXT_MAX_LENGTH } from "@components/utils/useContextSuggestion";

const props = defineProps({
  // Legacy PurposeViewEdit value. Prefer `context` (account.context / ER-9901).
  purpose: {
    type: String,
    default: "",
  },
  // idea-MDP-494: render ContextTab instead of PurposeViewEdit.
  showContextTabV2: {
    type: Boolean,
    default: false,
  },
  context: {
    type: String,
    default: null,
  },
  contextUpdatedAt: {
    type: String,
    default: null,
  },
  contextUpdatedBy: {
    type: [String, Number],
    default: null,
  },
  files: {
    type: Array,
    default: () => [],
  },
  uploadingFiles: {
    type: Array,
    default: () => [],
  },
  // ER-10219: Account.company_websites, edited one URL at a time.
  companyWebsites: {
    type: Array,
    default: () => [],
  },
  // ER-12781: scan status per website URL; empty when the status note is off.
  companyWebsiteScanStatuses: {
    type: Object,
    default: () => ({}),
  },
  companyWebsitesError: {
    type: String,
    default: "",
  },
  // ER-12785: show the section's Scan now button (owner/co-owner + idea-MDP-621, decided by the parent).
  canScanCompanyWebsites: {
    type: Boolean,
    default: false,
  },
  scanningCompanyWebsites: {
    type: Boolean,
    default: false,
  },
  savingCompanyWebsites: {
    type: Boolean,
    default: false,
  },
  // ER-10219: Account Context suggestion draft/error (ER-10218 pattern).
  accountContextDraft: {
    type: String,
    default: null,
  },
  accountContextSuggesting: {
    type: Boolean,
    default: false,
  },
  accountContextSuggestError: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:purpose",
  "save",
  "discard",
  "add-purpose",
  "edit",
  "improve",
  "suggest",
  "context-input",
  "context-discard",
  "add-website",
  "remove-website",
  "scan-websites",
  "upload",
  "remove",
]);

const breadcrumbsList = ref([
  { text: "Configuration", disabled: false },
  { text: "IQ Assistant", disabled: true },
]);

const purposeOrContext = computed(() => props.context || props.purpose || "");
const contextState = computed(() =>
  (props.context || "").trim() ? "filled" : "empty"
);

// ER-10219: with no company website saved, "Suggest with IQ" opens the URL
// prompt instead of suggesting straight away. Explain that on hover so the
// button does not read as broken.
const accountSuggestHint = computed(() =>
  props.companyWebsites.length
    ? ""
    : "No company website yet — click to paste one or more URLs and IQ will suggest the account context."
);

function handleSave(newValue) {
  emit("update:purpose", newValue);
  emit("save", newValue);
}

function handleContextSave(newValue) {
  emit("save", newValue);
}

function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === "Configuration") {
    emit("discard");
  }
}

function handleDiscard() {
  // Discard changes but stay in edit mode (don't navigate away).
}

function handleClose() {
  // Close edit mode without navigating away.
}
</script>

<style scoped>
.business-purpose-title {
  font-family: Inter;
  font-weight: 700;
  font-style: Bold;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  margin-left: 2px;
}
.role-purpose-tab {
  width: 100%;
}
.iq-assist-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  padding: 16px;
  margin-top: 24px;
  margin-bottom: 24px;
  box-shadow: 0px 1px 2px 0px #1018280D;
}
.iq-assist-banner-icon-img-wrapper {
  display: flex;
  padding: 10px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius:  8px;
  border: 1px solid #EAECF0;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.iq-assist-banner-icon-img {
  width: 100%;
}
.iq-assist-banner-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.iq-assist-banner-title {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.iq-assist-banner-description {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
</style>
